import type { Session } from "../types";

/** How many sessions the rail shows before "All N sessions…". */
export const RECENT_SESSION_COUNT = 3;

/**
 * The sessions belonging to one scenario, newest first.
 *
 * The sort is not cosmetic: showing "the recent three" requires an ordering
 * the server does not promise, so it is imposed here. Sorting a copy keeps
 * this safe to call on the store's array during render.
 */
export function scenarioSessions(
  sessions: Session[],
  scenarioId: string | undefined,
): Session[] {
  if (!scenarioId) return [];
  return sessions
    .filter((s) => s.scenarioId === scenarioId)
    .slice()
    .sort((a, b) => b.sessionNumber - a.sessionNumber);
}

/**
 * The window the rail renders. Collapsed and expanded are two states of one
 * list rather than two designs, so a short history looks identical either way
 * and the disclosure costs the common case nothing.
 */
export function visibleSessions(sessions: Session[], expanded: boolean): Session[] {
  return expanded ? sessions : sessions.slice(0, RECENT_SESSION_COUNT);
}

/** Whether the "All N sessions…" control has anything to reveal. */
export function isTruncated(sessions: Session[], expanded: boolean): boolean {
  return !expanded && sessions.length > RECENT_SESSION_COUNT;
}

/**
 * Falls back twice rather than once: a session opened before its scenario has
 * finished loading still needs a label, and an empty string in the rail reads
 * as a broken row.
 */
export function sessionDisplayName(
  session: Session,
  scenarioTitle: string | undefined,
): string {
  if (session.name) return session.name;
  return `${scenarioTitle || "Session"} #${session.sessionNumber}`;
}
